import React, { useRef, useState, useEffect } from "react";
import useAxiosPrivate from "hooks/useAxiosPrivate";
import { getUser } from "api/requests/getUser";
import { addUser } from "api/requests/addUser";
import { Card } from "@mantine/core";
// Chakra imports
import {
  Button,
  Box,
  Flex,
  FormControl,
  FormLabel,
  Grid,
  Icon,
  Input,
  Stack,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  useColorModeValue,
  useToast,
  Badge,
  Checkbox,
  Heading,
} from "@chakra-ui/react";
// Assets
import { BsCircleFill } from "react-icons/bs";

function NewUser() {
  const textColor = useColorModeValue("gray.600", "white");
  const inputColor = useColorModeValue("white", "#343A40");
  const bgColor = useColorModeValue("white", "#4A5058");
  const axiosPrivate = useAxiosPrivate();
  const toast = useToast();
  const [selectedRole, setSelectedRole] = useState("");
  const [user, setUser] = useState();
  const [isFormValid, setIsFormValid] = useState(false);
  const [activeBullets, setActiveBullets] = useState({
    userInfo: true,
    role: false,
  });
  const userInfoTab = useRef();
  const roleTab = useRef();
  const [formData, setFormData] = useState({
    firstname: "",
    lastname: "",
    email: "",
    username: "",
    password: "",
    roles: {
      User: true,
      Rep: false,
      Support: false,
      Creative: false,
      Billing: false,
      Search: false,
      Social: false,
      Manager: false,
      Admin: false,
      SuperAdmin: false,
    },
  });

  const rolesList = {
    User: {
      value: true,
      description: "Test Description.",
      color: "green",
    },
    Rep: {
      value: false,
      description: "Test Description.",
      color: "pink",
    },
    Support: {
      value: false,
      description: "Test Description.",
      color: "navy",
    },
    Creative: {
      value: false,
      description: "Test Description.",
      color: "teal",
    },
    Billing: {
      value: false,
      description: "Test Description.",
      color: "cyan",
    },
    Search: {
      value: false,
      description: "Test Description.",
      color: "yellow",
    },
    Social: {
      value: false,
      description: "Test Description.",
      color: "blue",
    },
    Manager: {
      value: false,
      description: "Test Description.",
      color: "orange",
    },
    Admin: {
      value: false,
      description: "Test Description.",
      color: "red",
    },
    SuperAdmin: {
      value: false,
      description: "Test Description.",
      color: "purple",
    },
  };

  useEffect(() => {
    // Validate form
    const isValid =
      formData.firstname &&
      formData.lastname &&
      formData.email &&
      formData.username &&
      formData.password;
    setIsFormValid(isValid);
  }, [formData]);

  useEffect(() => {
    let isMounted = true;
    const controller = new AbortController();

    const fetchUser = async () => {
      try {
        const userData = await getUser(axiosPrivate);
        setUser(userData);
        if (isMounted) {
          // Set state or perform actions with userData
        }
      } catch (error) {
        console.error("Error fetching user:", error);
      }
    };

    fetchUser();
    return () => {
      isMounted = false;
      controller.abort(); // Cancel the request if the component unmounts
    };
  }, [axiosPrivate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleRoleChange = (event) => {
    const role = event.target.name;
    console.log(role);
    setSelectedRole(role);

    setFormData((prev) => {
      const updatedRoles = {
        User: true,
        Rep: false,
        Support: false,
        Creative: false,
        Billing: false,
        Search: false,
        Social: false,
        Manager: false,
        Admin: false,
        SuperAdmin: false,
        ...prev.roles,
      };

      updatedRoles[role] = true;

      return {
        ...prev,
        roles: updatedRoles,
      };
    });
  };

  const handleAddUser = async () => {
    AWS.config.update({
      region: process.env.REACT_APP_AWS_REGION,
      accessKeyId: process.env.REACT_APP_S3_ACCESS_KEY_ID,
      secretAccessKey: process.env.REACT_APP_S3_SECRET_ACCESS_KEY,
    });

    const s3 = new AWS.S3();
    const userFolderKey = `users/${formData.username}`; // Ensure this ends with a slash
    const params = {
      Bucket: process.env.REACT_APP_S3_BUCKET_NAME,
      Key: userFolderKey,
      Body: "", // Empty content, simulating a folder
    };

    try {
      await s3.putObject(params).promise(); // If this fails, it will jump to the catch block
      const response = await addUser(axiosPrivate, {
        ...formData,
        imageDir: userFolderKey,
      });
      if (response?.status === 201) {
        toast({
          title: "Success!",
          description: "User added successfully.",
          status: "success",
          duration: 2000,
        });
        // Reset states as necessary
        setFormData({
          firstname: "",
          lastname: "",
          email: "",
          username: "",
          password: "",
          roles: {
            User: true,
            Manager: false,
            Admin: false,
            SuperAdmin: false,
          },
        });
        setSelectedRole("");
        setActiveBullets({
          userInfo: true,
          role: false,
        });
        userInfoTab.current.click();
      }
    } catch (error) {
      console.error(error); // Correct variable name for error logging
      toast({
        title: "Error!",
        description: "Error adding User.",
        status: "error",
        duration: 2000,
      });
    }
  };

  return (
    <Box
      direction="column"
      minH="89vh"
      align="center"
      pt={{ sm: "120px", md: "75px" }}
    >
      <Tabs variant="unstyled" mt="24px">
        <TabList display="flex" align="center" justifyContent="center">
          <Tab
            ref={userInfoTab}
            _focus={{}}
            w={{ sm: "80px", md: "200px" }}
            onClick={() =>
              setActiveBullets({
                userInfo: true,
                role: false,
              })
            }
          >
            <Flex
              direction="column"
              justify="center"
              align="center"
              position="relative"
              _before={{
                content: "''",
                width: { sm: "80px", md: "200px" },
                height: "3px",
                bg: activeBullets.role ? "white" : "blue.300",
                left: { sm: "12px", md: "32px" },
                top: { sm: activeBullets.userInfo ? "6px" : "4px", md: null },
                position: "absolute",
                bottom: activeBullets.userInfo ? "40px" : "38px",

                transition: "all .3s ease",
              }}
            >
              <Icon
                zIndex="1"
                as={BsCircleFill}
                color={activeBullets.userInfo ? "white" : "blue.300"}
                w={activeBullets.userInfo ? "16px" : "12px"}
                h={activeBullets.userInfo ? "16px" : "12px"}
                mb="8px"
              />
              <Text
                color={activeBullets.userInfo ? "white" : "blue.300"}
                fontWeight={activeBullets.userInfo ? "bold" : "normal"}
                display={{ sm: "none", md: "block" }}
              >
                User Info
              </Text>
            </Flex>
          </Tab>
          <Tab
            ref={roleTab}
            _focus={{}}
            w={{ sm: "80px", md: "200px" }}
            onClick={() =>
              setActiveBullets({
                userInfo: true,
                role: true,
              })
            }
          >
            <Flex direction="column" justify="center" align="center">
              <Icon
                zIndex="1"
                as={BsCircleFill}
                color={activeBullets.role ? "white" : "blue.300"}
                w={activeBullets.role ? "16px" : "12px"}
                h={activeBullets.role ? "16px" : "12px"}
                mb="8px"
              />
              <Text
                color={activeBullets.role ? "white" : "gray.300"}
                fontWeight={activeBullets.role ? "bold" : "normal"}
                transition="all .3s ease"
                _hover={{ color: "white" }}
                display={{ sm: "none", md: "block" }}
              >
                Role
              </Text>
            </Flex>
          </Tab>
        </TabList>
        <TabPanels mt="24px">
          <TabPanel>
            <Card bg={bgColor} mb={"md"} withBorder>
              <Heading mb="40px">
                <Flex direction="column">
                  <Text
                    color={textColor}
                    fontSize="lg"
                    fontWeight="bold"
                    mb="3px"
                  >
                    User Info
                  </Text>
                  <Text color="gray.400" fontWeight="normal" fontSize="sm">
                    Mandatory Information
                  </Text>
                </Flex>
              </Heading>
              <Box>
                <Flex direction="column" w="100%">
                  <Grid
                    templateColumns={{ sm: "1fr", md: "repeat(2, 1fr)" }}
                    templateRows={{ md: "repeat(2, 1fr)" }}
                    gap="24px"
                  >
                    <FormControl isRequired>
                      <FormLabel
                        color={textColor}
                        fontWeight="bold"
                        fontSize="xs"
                      >
                        First Name
                      </FormLabel>
                      <Input
                        variant="main"
                        color={textColor}
                        bg={inputColor}
                        name="firstname"
                        placeholder="eg. Michael"
                        fontSize="xs"
                        value={formData.firstname}
                        onChange={handleChange}
                        required
                      />
                    </FormControl>
                    <FormControl isRequired>
                      <FormLabel
                        color={textColor}
                        fontWeight="bold"
                        fontSize="xs"
                      >
                        Last Name
                      </FormLabel>
                      <Input
                        variant="main"
                        color={textColor}
                        bg={inputColor}
                        name="lastname"
                        placeholder="eg. Jackson"
                        fontSize="xs"
                        value={formData.lastname}
                        onChange={handleChange}
                        required
                      />
                    </FormControl>
                    <FormControl isRequired>
                      <FormLabel
                        color={textColor}
                        fontWeight="bold"
                        fontSize="xs"
                      >
                        Username
                      </FormLabel>
                      <Input
                        variant="main"
                        color={textColor}
                        bg={inputColor}
                        name="username"
                        placeholder="eg. michaeljackson"
                        fontSize="xs"
                        value={formData.username}
                        onChange={handleChange}
                        required
                      />
                    </FormControl>
                    <FormControl isRequired>
                      <FormLabel
                        color={textColor}
                        fontWeight="bold"
                        fontSize="xs"
                      >
                        Email address
                      </FormLabel>
                      <Input
                        variant="main"
                        color={textColor}
                        bg={inputColor}
                        type="email"
                        name="email"
                        placeholder="eg. example@yahoo.com"
                        fontSize="xs"
                        value={formData.email}
                        onChange={handleChange}
                        required
                      />
                    </FormControl>
                    <FormControl isRequired>
                      <FormLabel
                        color={textColor}
                        fontWeight="bold"
                        fontSize="xs"
                      >
                        Password
                      </FormLabel>
                      <Input
                        variant="main"
                        color={textColor}
                        bg={inputColor}
                        type="password"
                        name="password"
                        placeholder="******"
                        fontSize="xs"
                        value={formData.password}
                        onChange={handleChange}
                        required
                      />
                    </FormControl>
                  </Grid>
                  <Button
                    alignSelf="flex-end"
                    mt="24px"
                    w="100px"
                    h="35px"
                    onClick={() => roleTab.current.click()}
                    fontSize={"14px"}
                  >
                    NEXT
                  </Button>
                </Flex>
              </Box>
            </Card>
          </TabPanel>
          <TabPanel>
            <Card bg={bgColor} mb={"md"} withBorder>
              <Heading mb="40px">
                <Text
                  color={textColor}
                  fontSize="lg"
                  fontWeight="bold"
                  mb="3px"
                >
                  Roles
                </Text>
              </Heading>
              <Box>
                <Flex direction="column" w="100%">
                  <Stack direction="column" spacing="24px" pb={2}>
                    <Text>Select a role for the user</Text>
                  </Stack>
                  <Grid templateColumns="0.3fr auto 2fr" gap="2px">
                    {Object.entries(rolesList).map(
                      ([role, { value, description, color }]) => (
                        <React.Fragment key={role}>
                          <Box
                            key={"role1"}
                            border="1px solid"
                            borderColor="gray.400"
                            p={2}
                          >
                            <Badge colorScheme={color}>{role}</Badge>
                          </Box>
                          <Box
                            key={"role2"}
                            display="flex"
                            justifyContent="center"
                            alignItems="center"
                            border="1px solid"
                            borderColor="gray.400"
                            p={2}
                          >
                            <Checkbox
                              name={role}
                              defaultChecked={value}
                              onChange={(e) => handleRoleChange(e)}
                            />
                          </Box>
                          <Box
                            key={"role3"}
                            border="1px solid"
                            borderColor="gray.400"
                            p={2}
                          >
                            <Text textAlign="left">{description}</Text>
                          </Box>
                        </React.Fragment>
                      )
                    )}
                  </Grid>
                  <Flex justify="space-between">
                    <Button
                      alignSelf="flex-end"
                      mt="24px"
                      w="100px"
                      h="35px"
                      onClick={() => userInfoTab.current.click()}
                      fontSize={"14px"}
                    >
                      PREV
                    </Button>
                    <Button
                      colorScheme={"blue"}
                      mt="24px"
                      w="100px"
                      h="35px"
                      isDisabled={!isFormValid}
                      onClick={() => {
                        console.log(formData);
                        handleAddUser();
                      }}
                    >
                      Create
                    </Button>
                  </Flex>
                </Flex>
              </Box>
            </Card>
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Box>
  );
}

export default NewUser;
